import { useState } from 'react'
import { UserPlus, UsersThree } from '@phosphor-icons/react'
import { EmptyState, Field } from '@/ui'
import type { UserOption } from '../api/reference'
import { useAddMember, useLocationMembers, useRemoveMember, type Member } from '../api/store'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { FormDialog } from './FormDialog'
import { PersonPicker } from './PersonPicker'
import { PinDialog } from './PhoneTeam'
import { TeamTable } from './TeamTable'
import { QueryState } from './Layout'
import { Section } from './bits'

/** The site team: the people whose phones may record store work at this site. */
export function LocationTeam({ siteId, siteName }: { siteId: string; siteName: string }) {
  const q = useLocationMembers(siteId)
  const add = useAddMember()
  const remove = useRemoveMember()
  const [adding, setAdding] = useState(false)
  const [person, setPerson] = useState<UserOption | null>(null)
  const [removing, setRemoving] = useState<Member | null>(null)
  const [pinFor, setPinFor] = useState<Member | null>(null)
  const members = q.data ?? []

  return (
    <Section
      title="Site team"
      eyebrow={siteName}
      actions={
        <ActionButton
          perm="locations:manage"
          hide
          size="sm"
          variant="secondary"
          leadingIcon={<UserPlus />}
          onClick={() => {
            setPerson(null)
            setAdding(true)
          }}
        >
          Add
        </ActionButton>
      }
    >
      <QueryState query={q} label="the team">
        {members.length === 0 ? (
          <EmptyState icon={<UsersThree />} title="No one yet" description="Add the keeper and anyone else who works the store phones here." />
        ) : (
          <TeamTable rows={members} onPin={setPinFor} onRemove={setRemoving} removePerm="locations:manage" compact />
        )}
      </QueryState>

      {adding && (
        <FormDialog
          open
          onClose={() => setAdding(false)}
          title="Add to the site team"
          description={`Their phone can then record store work at ${siteName}.`}
          saveLabel="Add"
          loading={add.isPending}
          canSave={Boolean(person) && !members.some((m) => m.userId === person?.id)}
          onSave={() => add.mutateAsync({ id: siteId, userId: person!.id })}
        >
          <Field
            label="Person"
            required
            error={person && members.some((m) => m.userId === person.id) ? 'Already on the team' : undefined}
          >
            <PersonPicker value={person} onChange={setPerson} label="Person" />
          </Field>
        </FormDialog>
      )}
      {pinFor && (
        <PinDialog userId={pinFor.userId} name={pinFor.name || 'this person'} hasPin={pinFor.hasPin} onClose={() => setPinFor(null)} />
      )}
      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? ''} from the team?`}
        description="Their phone can no longer record store work at this site."
        confirmLabel="Remove"
        tone="danger"
        loading={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync({ id: siteId, userId: removing!.userId })
          setRemoving(null)
        }}
      />
    </Section>
  )
}
